<template>
    <div>
        <!-- Botón para abrir el modal -->
        <slot name="trigger" :open="openModal">
            <ArgonButton variant="gradient" color="success" size="md" class="w-100" @click="openModal">
                <i class="fas fa-pills me-2"></i>
                {{ label }}
            </ArgonButton>
        </slot>

        <!-- Modal -->
        <teleport to="body">
            <transition name="modal-fade">
                <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
                    <div class="modal-container">
                        <!-- Header del modal -->
                        <div class="modal-header">
                            <div class="header-left">
                                <div class="icon-wrapper">
                                    <i class="fas fa-prescription-bottle-alt"></i>
                                </div>
                                <div>
                                    <h4 class="modal-title">Selector de Medicamentos</h4>
                                    <p class="modal-subtitle">Busca, agrega o edita medicamentos</p>
                                </div>
                            </div>
                            <button class="close-btn" @click="closeModal">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>

                        <!-- Barra de búsqueda y filtros -->
                        <div v-if="!showForm" class="search-section">
                            <div class="search-wrapper">
                                <i class="fas fa-search search-icon"></i>
                                <input
v-model="searchQuery" placeholder="Buscar medicamentos..." class="search-input"
                                    autofocus />
                                <button
v-if="searchQuery || selectedLetter" class="clear-search-btn"
                                    @click="clearSearch">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>

                            <!-- Alfabeto -->
                            <div class="alphabet-section">
                                <div class="alphabet-buttons">
                                    <button
v-for="letter in alphabet" :key="letter"
                                        :class="['alphabet-btn', { 'active': selectedLetter === letter }]"
                                        @click="filterByLetter(letter)">
                                        {{ letter }}
                                    </button>
                                </div>
                            </div>

                            <!-- Botón para mostrar formulario -->
                            <div v-if="canEditAndAdd" class="show-form-section">
                                <ArgonButton
variant="gradient" color="success" size="md" class="w-100"
                                    @click="showFormHandler">
                                    <i class="fas fa-plus-circle me-2"></i>
                                    Agregar Nuevo Medicamento
                                </ArgonButton>
                            </div>
                        </div>

                        <!-- Lista de medicamentos -->
                        <transition name="slide-fade">
                            <div v-if="!showForm" class="medicines-section">
                                <div v-if="medicines.length === 0 && !isLoadingMore" class="empty-state">
                                    <i class="fas fa-pills empty-icon"></i>
                                    <p class="empty-text">No se encontraron medicamentos</p>
                                    <p class="empty-subtext">Prueba con otra búsqueda o agrega uno nuevo</p>
                                </div>

                                <div v-else ref="scrollableOptions" class="medicines-list" @scroll="onScroll">
                                    <div
v-for="item in medicines" :key="item.id"
                                        :class="['medicine-card', { 'selected': selectedMedicineId === item.id }]"
                                        @click="selectMedicine(item)">
                                        <div class="medicine-content">
                                            <div class="medicine-name">
                                                <i class="fas fa-pills me-2"></i>
                                                <strong>{{ item.name }}</strong>
                                            </div>
                                            <div class="medicine-description">{{ item.description }}</div>
                                        </div>
                                        <div v-if="canEditAndAdd" class="medicine-actions">
                                            <button
class="action-btn edit-btn" title="Editar"
                                                @click.stop="editMedicine(item)">
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button
class="action-btn delete-btn" title="Eliminar"
                                                @click.stop="handleDelete(item.id)">
                                                <i class="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="isLoadingMore" class="loading-more">
                                        <i class="fas fa-spinner fa-spin me-2"></i>
                                        Cargando más...
                                    </div>
                                </div>
                            </div>
                        </transition>

                        <!-- Formulario de agregar/editar -->
                        <transition name="slide-fade">
                            <div v-if="showForm && canEditAndAdd" class="form-section-full">
                                <div class="form-header-full">
                                    <div class="form-header-left">
                                        <i :class="isEditing ? 'fas fa-edit' : 'fas fa-plus-circle'" class="me-2"></i>
                                        {{ isEditing ? 'Editar Medicamento' : 'Agregar Nuevo Medicamento' }}
                                    </div>
                                    <button class="back-btn" @click="hideFormHandler">
                                        <i class="fas fa-arrow-left me-2"></i>
                                        Volver al Listado
                                    </button>
                                </div>
                                <div class="form-content">
                                    <div class="form-grid">
                                        <div class="form-group full-width">
                                            <label class="form-label">
                                                <i class="fas fa-capsules me-1"></i>
                                                Nombre del Medicamento
                                                <span class="required-star">*</span>
                                            </label>
                                            <input
v-model="newMedicineName" placeholder="Ej: Paracetamol 500mg"
                                                class="form-input" autofocus />
                                        </div>
                                        <div class="form-group full-width">
                                            <label class="form-label">
                                                <i class="fas fa-prescription me-1"></i>
                                                Prescripción / Descripción
                                                <span class="required-star">*</span>
                                            </label>
                                            <textarea
v-model="newMedicineDescription"
                                                placeholder="Ej: 1 tableta cada 8 horas por 5 días"
                                                class="form-textarea" rows="6"></textarea>
                                        </div>
                                    </div>
                                    <div class="form-actions">
                                        <ArgonButton
variant="gradient" :color="isEditing ? 'warning' : 'success'"
                                            size="lg"
                                            :disabled="!newMedicineName.trim() || !newMedicineDescription.trim()"
                                            @click="isEditing ? updateMedicine() : addMedicine()">
                                            <i :class="isEditing ? 'fas fa-save' : 'fas fa-check'" class="me-2"></i>
                                            {{ isEditing ? 'Guardar Cambios' : 'Agregar Medicamento' }}
                                        </ArgonButton>
                                        <ArgonButton
variant="outline" color="secondary" size="lg"
                                            @click="hideFormHandler">
                                            <i class="fas fa-times me-2"></i>
                                            Cancelar
                                        </ArgonButton>
                                    </div>
                                </div>
                            </div>
                        </transition>

                        <!-- Footer del modal -->
                        <div class="modal-footer">
                            <!-- Barra de medicamentos agregados -->
                            <transition name="slide-up">
                                <div v-if="addedMedicines.length > 0" class="added-medicines-footer">
                                    <div class="added-medicines-title-footer">
                                        <i class="fas fa-check-circle"></i>
                                        <span>Medicamentos Agregados ({{ addedMedicines.length }})</span>
                                    </div>
                                    <div class="added-medicines-list-footer">
                                        <transition-group name="list-item">
                                            <div
v-for="med in addedMedicines" :key="med.timestamp"
                                                class="added-medicine-item-footer">
                                                <i class="fas fa-pills"></i>
                                                <span>{{ med.name }}</span>
                                            </div>
                                        </transition-group>
                                    </div>
                                </div>
                            </transition>

                            <ArgonButton variant="outline" color="secondary" size="md" @click="closeModal">
                                <i class="fas fa-times me-2"></i>
                                Cerrar
                            </ArgonButton>
                        </div>
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance } from 'vue'
import api from '@/services/api'
import ArgonButton from '@/components/ArgonButton.vue'
import Swal from 'sweetalert2'

const { proxy } = getCurrentInstance()

const props = defineProps({
    label: {
        type: String,
        default: 'Agregar Medicamento'
    },
    apiurl: {
        type: String,
        default: 'medicines'
    },
    canEditAndAdd: {
        type: Boolean,
        default: true
    }
})

const emit = defineEmits(['medicine-selected'])

// Estado del modal
const isModalOpen = ref(false)
const showForm = ref(false)
const addedMedicines = ref([]) // Lista de medicamentos agregados en esta sesión

// Datos
const allMedicines = ref([])
const medicines = ref([])
const selectedMedicineId = ref(null)
const searchQuery = ref('')
const selectedLetter = ref('')

// Formulario
const newMedicineName = ref('')
const newMedicineDescription = ref('')
const isEditing = ref(false)
const editingMedicineId = ref(null)

// Paginación
const isLoadingMore = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(100)
const totalPages = ref(1)
const scrollableOptions = ref(null)

// Alfabeto
const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

// Funciones de utilidad
const normalizeString = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

const normalizeData = (data) => {
    return data.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        normalizedName: normalizeString(item.name),
        deleted_at: item.deleted_at || null,
    }))
}

const saveToLocalStorage = (data) => {
    localStorage.setItem('medicines', JSON.stringify(data))
}

const loadFromLocalStorage = () => {
    const data = localStorage.getItem('medicines')
    return data ? JSON.parse(data) : []
}

// Modal
const openModal = async () => {
    isModalOpen.value = true
    document.body.style.overflow = 'hidden'

    // Cargar desde localStorage primero
    if (allMedicines.value.length === 0) {
        allMedicines.value = loadFromLocalStorage()
    }

    // Actualizar la lista filtrada
    updateMedicines()

    // Si no hay datos en localStorage, cargar desde API
    if (allMedicines.value.length === 0) {
        await fetchAllMedicines()
    }
}

const closeModal = () => {
    isModalOpen.value = false
    showForm.value = false
    addedMedicines.value = [] // Limpiar lista al cerrar
    document.body.style.overflow = ''
    resetEditState()
    searchQuery.value = ''
    selectedLetter.value = ''
}

// Mostrar/ocultar formulario
const showFormHandler = () => {
    showForm.value = true
    resetEditState()
}

const hideFormHandler = () => {
    showForm.value = false
    resetEditState()
}

// Búsqueda y filtros
const clearSearch = () => {
    searchQuery.value = ''
    selectedLetter.value = ''
    fetchAllMedicines(1)
}

const filterByLetter = (letter) => {
    selectedLetter.value = letter
    searchQuery.value = ''
    fetchAllMedicines(1, letter)
}

// API
const fetchAllMedicines = async (page = 1, firstLetter = '') => {
    try {
        isLoadingMore.value = true
        let url = `/${props.apiurl}?page=${page}&per_page=${itemsPerPage.value}`
        if (searchQuery.value) {
            url += `&search=${normalizeString(searchQuery.value)}`
        } else if (firstLetter) {
            url += `&first_letter=${firstLetter}`
        }

        const response = await api.get(url)
        const newItems = normalizeData(response.data.data)
        allMedicines.value = page === 1 ? newItems : [...allMedicines.value, ...newItems]

        saveToLocalStorage(allMedicines.value)
        totalPages.value = response.data.pagination.last_page

        updateMedicines()
    } catch (error) {
        console.error('Error fetching medicines:', error)
        proxy.$notify('Error al cargar medicamentos', '', 'danger', 'ni ni-fat-remove ni-lg')
    } finally {
        isLoadingMore.value = false
    }
}

const updateMedicines = () => {
    const filteredItems = allMedicines.value.filter(item => {
        const notDeleted = item.deleted_at === null || item.deleted_at === undefined
        const matchesSearch = !searchQuery.value || item.normalizedName.includes(normalizeString(searchQuery.value))
        return notDeleted && matchesSearch
    })
    medicines.value = filteredItems
}

// Selección
const selectMedicine = (item) => {
    selectedMedicineId.value = item.id
    emit('medicine-selected', { name: item.name, description: item.description })

    // Agregar a la lista de medicamentos agregados
    addedMedicines.value.push({
        id: item.id,
        name: item.name,
        timestamp: Date.now()
    })

    proxy.$notify(`${item.name} agregado a la receta`, '', 'success', 'ni ni-like-2 ni-lg')
    // No cerrar el modal para permitir agregar más medicamentos
}

// CRUD
const addMedicine = async () => {
    if (!newMedicineName.value.trim() || !newMedicineDescription.value.trim()) return

    // Verificar si existe
    let existingItem = allMedicines.value.find(item =>
        item.normalizedName === normalizeString(newMedicineName.value.trim())
    )

    if (existingItem) {
        if (existingItem.deleted_at !== null) {
            try {
                await api.post(`/${props.apiurl}/${existingItem.id}/restore`)
                existingItem.deleted_at = null
                saveToLocalStorage(allMedicines.value)
            } catch (error) {
                console.error('Error restoring medicine:', error)
            }
        }
        selectMedicine(existingItem)
        resetEditState()
        return
    }

    try {
        const response = await api.post(`/${props.apiurl}`, {
            name: newMedicineName.value,
            description: newMedicineDescription.value
        })
        const newItem = response.data.data
        allMedicines.value.push(normalizeData([newItem])[0])
        allMedicines.value.sort((a, b) => a.normalizedName.localeCompare(b.normalizedName))
        saveToLocalStorage(allMedicines.value)

        selectMedicine(newItem)
        resetEditState()
        updateMedicines()
        proxy.$notify('Medicamento creado y agregado a la receta', '', 'success', 'ni ni-like-2 ni-lg')
        showForm.value = false
    } catch (error) {
        console.error('Error adding medicine:', error)
        proxy.$notify('Error al agregar medicamento', '', 'danger', 'ni ni-fat-remove ni-lg')
    }
}

const editMedicine = (item) => {
    newMedicineName.value = item.name
    newMedicineDescription.value = item.description
    isEditing.value = true
    editingMedicineId.value = item.id
    showForm.value = true
}

const updateMedicine = async () => {
    if (!newMedicineName.value.trim() || !newMedicineDescription.value.trim()) return

    try {
        const response = await api.put(`/${props.apiurl}/${editingMedicineId.value}`, {
            name: newMedicineName.value,
            description: newMedicineDescription.value
        })
        const updatedItem = response.data.data
        const index = allMedicines.value.findIndex(item => item.id === editingMedicineId.value)
        if (index !== -1) {
            allMedicines.value[index] = normalizeData([updatedItem])[0]
        }

        saveToLocalStorage(allMedicines.value)
        resetEditState()
        updateMedicines()
        proxy.$notify('Medicamento actualizado exitosamente', '', 'success', 'ni ni-like-2 ni-lg')
        showForm.value = false
    } catch (error) {
        console.error('Error updating medicine:', error)
        proxy.$notify('Error al actualizar medicamento', '', 'danger', 'ni ni-fat-remove ni-lg')
    }
}

const handleDelete = async (id) => {
    const medicine = medicines.value.find(item => item.id === id)
    if (!medicine) return

    const result = await Swal.fire({
        title: '¿Estás seguro?',
        html: `¡No podrás revertir esto!<br>¿Deseas eliminar el medicamento, <b>${medicine.name}</b>?`,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sí, ¡elimínalo!',
        customClass: {
            confirmButton: 'btn bg-gradient-success',
            cancelButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
    })

    if (result.isConfirmed) {
        try {
            await api.delete(`/${props.apiurl}/${id}`)
            const itemToDelete = allMedicines.value.find(item => item.id === id)
            if (itemToDelete) {
                itemToDelete.deleted_at = new Date().toISOString()
            }
            saveToLocalStorage(allMedicines.value)
            updateMedicines()
            proxy.$notify('Medicamento eliminado exitosamente', '', 'success', 'ni ni-like-2 ni-lg')
        } catch (error) {
            console.error('Error deleting medicine:', error)
            proxy.$notify('Error al eliminar medicamento', '', 'danger', 'ni ni-fat-remove ni-lg')
        }
    }
}

const resetEditState = () => {
    newMedicineName.value = ''
    newMedicineDescription.value = ''
    isEditing.value = false
    editingMedicineId.value = null
}

// Scroll infinito
const onScroll = async () => {
    if (scrollableOptions.value) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableOptions.value
        if (scrollTop + clientHeight >= scrollHeight - 10 && currentPage.value < totalPages.value) {
            currentPage.value += 1
            await fetchAllMedicines(currentPage.value)
        }
    }
}

// Watchers
watch(searchQuery, (newValue, oldValue) => {
    // Solo buscar si hay un cambio real en la búsqueda
    if (newValue !== oldValue) {
        if (newValue) {
            currentPage.value = 1
            fetchAllMedicines()
        } else {
            // Si se limpia la búsqueda, solo actualizar el filtro
            updateMedicines()
        }
    }
})

onMounted(() => {
    const localData = loadFromLocalStorage()
    if (localData && localData.length > 0) {
        allMedicines.value = localData
        updateMedicines()
    }
})
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

/* Modal Container */
.modal-container {
    background: white;
    border-radius: 20px;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Modal Header */
.modal-header {
    background: linear-gradient(135deg, #16a085 0%, #11998e 100%);
    border-radius: 20px 20px 0 0;
    padding: 1.75rem 2rem;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    overflow: hidden;
}

.modal-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
    background-size: 20px 20px;
    pointer-events: none;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    z-index: 1;
}

.icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    border: 1.5px solid rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
}

.modal-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.modal-subtitle {
    margin: 0.25rem 0 0 0;
    opacity: 0.95;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.92);
}

.close-btn {
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 0.2);
    border: 1.5px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
}

/* Barra de Medicamentos Agregados */
.added-medicines-footer {
    flex: 1;
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    border: 2px solid #10b981;
    border-radius: 12px;
    padding: 0.75rem;
    margin-right: 1rem;
}

.added-medicines-title-footer {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    font-weight: 700;
    color: #065f46;
    margin-bottom: 0.4rem;
}

.added-medicines-title-footer i {
    color: #10b981;
    font-size: 0.85rem;
}

.added-medicines-list-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
}

.added-medicine-item-footer {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    background: white;
    border: 1px solid #10b981;
    border-radius: 12px;
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #065f46;
    box-shadow: 0 1px 3px rgba(16, 185, 129, 0.12);
    animation: bounceIn 0.5s ease;
    line-height: 1.2;
}

.added-medicine-item-footer i {
    color: #10b981;
    font-size: 0.7rem;
}

/* Eliminar estilos antiguos de la barra superior */
.added-medicines-bar {
    display: none;
}

@keyframes bounceIn {
    0% {
        opacity: 0;
        transform: scale(0.3);
    }

    50% {
        opacity: 1;
        transform: scale(1.05);
    }

    70% {
        transform: scale(0.9);
    }

    100% {
        transform: scale(1);
    }
}

.added-medicines-bar::-webkit-scrollbar {
    height: 6px;
}

.added-medicines-bar::-webkit-scrollbar-track {
    background: #d1fae5;
}

.added-medicines-bar::-webkit-scrollbar-thumb {
    background: #10b981;
    border-radius: 3px;
}

.added-medicines-bar::-webkit-scrollbar-thumb:hover {
    background: #059669;
}

/* Search Section */
.search-section {
    padding: 1.25rem 2rem 1rem 2rem;
    background: linear-gradient(135deg, #f7fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #e2e8f0;
}

.show-form-section {
    margin-top: 0.75rem;
}

.search-wrapper {
    position: relative;
    margin-bottom: 0.75rem;
}

.search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #10b981;
    font-size: 1.1rem;
}

.search-input {
    width: 100%;
    padding: 0.875rem 3rem 0.875rem 3rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.search-input:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.clear-search-btn {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    background: #e2e8f0;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
}

.clear-search-btn:hover {
    background: #cbd5e1;
    color: #475569;
}

/* Alfabeto */
.alphabet-section {
    margin-top: 0.75rem;
}

.alphabet-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    justify-content: center;
    padding: 0;
}

.alphabet-btn {
    min-width: 26px;
    height: 26px;
    padding: 0.15rem 0.3rem;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.alphabet-btn:hover {
    border-color: #10b981;
    color: #10b981;
    background: rgba(16, 185, 129, 0.05);
}

.alphabet-btn.active {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: #10b981;
    color: white;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

/* Medicines Section */
.medicines-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.medicines-list {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 2rem;
}

.medicines-list::-webkit-scrollbar {
    width: 8px;
}

.medicines-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.medicines-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.medicines-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.medicine-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.medicine-card:hover {
    border-color: #10b981;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
    transform: translateY(-2px);
}

.medicine-card.selected {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
    border-color: #10b981;
}

.medicine-content {
    flex: 1;
    min-width: 0;
}

.medicine-name {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    color: #1e293b;
    margin-bottom: 0.25rem;
}

.medicine-name i {
    color: #10b981;
}

.medicine-description {
    font-size: 0.8rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
}

.medicine-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: 1rem;
}

.action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.85rem;
}

.edit-btn {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
}

.edit-btn:hover {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    transform: translateY(-2px);
}

.delete-btn {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
}

.delete-btn:hover {
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
    transform: translateY(-2px);
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
}

.empty-icon {
    font-size: 4rem;
    color: #cbd5e1;
    margin-bottom: 1rem;
}

.empty-text {
    font-size: 1.25rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.5rem;
}

.empty-subtext {
    font-size: 0.95rem;
    color: #94a3b8;
}

.loading-more {
    text-align: center;
    padding: 1rem;
    color: #10b981;
    font-weight: 500;
}

/* Form Section */
.form-section-full {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.form-header-full {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%);
    border-bottom: 2px solid #10b981;
}

.form-header-left {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
}

.form-header-left i {
    color: #10b981;
    font-size: 1.4rem;
}

.back-btn {
    display: flex;
    align-items: center;
    padding: 0.65rem 1.25rem;
    background: white;
    border: 2px solid #10b981;
    border-radius: 10px;
    color: #10b981;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.back-btn:hover {
    background: #10b981;
    color: white;
    transform: translateX(-3px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.form-content {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
    background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
}

.form-content::-webkit-scrollbar {
    width: 8px;
}

.form-content::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.form-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.form-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.form-section {
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%);
    border-top: 1px solid #e2e8f0;
}

.form-header-mini {
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 1rem;
}

.form-header-mini i {
    color: #10b981;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group.full-width {
    grid-column: 1 / -1;
}

.form-label {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.65rem;
}

.form-label i {
    color: #10b981;
    font-size: 1.1rem;
}

.required-star {
    color: #ef4444;
    margin-left: 0.25rem;
    font-weight: 700;
}

.form-input,
.form-textarea {
    padding: 1rem 1.25rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.form-input:focus,
.form-textarea:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.15);
}

.form-textarea {
    resize: vertical;
    font-family: inherit;
}

.form-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    padding-top: 1rem;
    border-top: 2px solid #e2e8f0;
}

/* Modal Footer */
.modal-footer {
    padding: 1rem 2rem;
    background: #f8fafc;
    border-radius: 0 0 20px 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 1rem;
}

/* Transiciones */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .modal-container {
    animation: modalSlideIn 0.3s ease;
}

.modal-fade-leave-active .modal-container {
    animation: modalSlideOut 0.3s ease;
}

.slide-fade-enter-active {
    animation: slideInFromBottom 0.4s ease;
}

.slide-fade-leave-active {
    animation: slideOutToBottom 0.4s ease;
}

@keyframes slideInFromBottom {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideOutToBottom {
    from {
        opacity: 1;
        transform: translateY(0);
    }

    to {
        opacity: 0;
        transform: translateY(30px);
    }
}

.slide-down-enter-active {
    animation: slideDownIn 0.4s ease;
}

.slide-down-leave-active {
    animation: slideDownOut 0.4s ease;
}

@keyframes slideDownIn {
    from {
        opacity: 0;
        max-height: 0;
        transform: translateY(-20px);
    }

    to {
        opacity: 1;
        max-height: 200px;
        transform: translateY(0);
    }
}

@keyframes slideDownOut {
    from {
        opacity: 1;
        max-height: 200px;
        transform: translateY(0);
    }

    to {
        opacity: 0;
        max-height: 0;
        transform: translateY(-20px);
    }
}

.slide-up-enter-active {
    animation: slideUpIn 0.4s ease;
}

.slide-up-leave-active {
    animation: slideUpOut 0.4s ease;
}

@keyframes slideUpIn {
    from {
        opacity: 0;
        max-height: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        max-height: 150px;
        transform: translateY(0);
    }
}

@keyframes slideUpOut {
    from {
        opacity: 1;
        max-height: 150px;
        transform: translateY(0);
    }

    to {
        opacity: 0;
        max-height: 0;
        transform: translateY(20px);
    }
}

.list-item-enter-active {
    animation: bounceIn 0.5s ease;
}

.list-item-leave-active {
    animation: fadeOut 0.3s ease;
}

@keyframes fadeOut {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}

@keyframes modalSlideOut {
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    to {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .modal-container {
        max-width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }

    .modal-header {
        border-radius: 0;
        padding: 1.25rem 1rem;
    }

    .added-medicines-footer {
        margin-right: 0;
        margin-bottom: 0.75rem;
        padding: 0.65rem;
    }

    .added-medicines-title-footer {
        font-size: 0.75rem;
        margin-bottom: 0.35rem;
    }

    .added-medicines-title-footer i {
        font-size: 0.8rem;
    }

    .added-medicine-item-footer {
        font-size: 0.65rem;
        padding: 0.2rem 0.4rem;
        gap: 0.2rem;
    }

    .added-medicine-item-footer i {
        font-size: 0.65rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
        padding: 1rem;
    }

    .modal-footer button {
        width: 100%;
    }

    .search-section {
        padding: 1rem;
    }

    .medicines-list {
        padding: 1rem;
    }

    .form-content {
        padding: 1.5rem 1rem;
    }

    .form-section {
        padding: 1rem;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }

    .alphabet-buttons {
        justify-content: center;
        gap: 0.25rem;
    }

    .alphabet-btn {
        min-width: 24px;
        height: 24px;
        font-size: 0.7rem;
        padding: 0.1rem 0.25rem;
    }

    .form-header-full {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }

    .form-header-left {
        justify-content: center;
    }

    .back-btn {
        width: 100%;
        justify-content: center;
    }

    .form-actions {
        flex-direction: column;
    }

    .form-actions>* {
        width: 100%;
    }
}
</style>
